import { asyncScheduler, BehaviorSubject, iif, Observable, of, scheduled } from 'rxjs';
import { filter, finalize, map, mergeMap, share, take } from 'rxjs/operators';
import { VOID } from './void';

/**
 * Function that returns an Observable which provides the new state value when resolved.
 */
export type StateSubjectRefresher<T> = () => Observable<T>;

export enum StateObservableOperationMode {
	/** Instruct the StateObservable to emit a single value and then complete. */
	EmitOnce,
	/** Instruct the StateObservable to emit all changes until the subscription is canceled. */
	Watch,
}

/**
 * A variant of BehaviorSubject that updates the value by using a StateSubjectRefresher function.
 *
 * Noticeable differences:
 *  * The `initialize()` method can be used to update when a default value has not been provided.
 *  * The `refresh()` method can be used to update the value using the refresher.
 *      * Try to use `next()` only when another request has provided a value.
 *  * Both methods mentioned above return an observable that completes after emitting the first value.
 *  * It has been designed to work with strictNullChecks and to avoid dealing with undefined values
 *    after initialization:
 *      * asStateObservable() can be used instead of asObservable().
 *      * getStateValue() can be used instead of getValue().
 *      * stateValue can be used instead of value.
 */
export class StateSubject<T> extends BehaviorSubject<T | undefined> {
	private sharedRefresher$: Observable<void> | undefined;

	public get initialized(): boolean {
		return (this as any).value !== undefined;
	}

	/**
	 * Contains the current stateValue, throws if the state has not been initialized.
	 */
	public get stateValue(): T {
		if ((this as any).value !== undefined) {
			return (this as any).value;
		}
		if (this.sharedRefresher$ !== undefined) {
			throw new Error('The StateSubject initialization did not finish!');
		}
		throw new Error('The StateSubject has not been initialized!');
	}

	/**
	 * @param refresher A StateSubjectRefresher function that returns an Observable which provides
	 *                  the new state value when resolved.
	 * @param initialValue An optional initial value.
	 */
	constructor(private refresher: StateSubjectRefresher<T>, initialValue?: T) {
		super(initialValue);
	}

	/**
	 * Initializes the state and completes after one value has been emitted.
	 *
	 * On subsequent calls it just returns the existing state, use refresh() to refresh the value.
	 */
	public initialize(): Observable<T> {
		return iif(() => this.initialized, VOID, this.fetchAndUpdateStateValue()).pipe(map(() => this.stateValue));
	}

	/**
	 * Returns an observable that emits the state value. The observable it will either emit once and
	 * then complete if `StateObservableOperationMode.EmitOnce` is provided as argument. By default
	 * the observable will continuously emit changes and NEVER complete.
	 *
	 * It's not necessary to initialize the state before using this method.
	 *
	 * Only defined values are emitted, use `asObservable()` to also receive the undefined values.
	 *
	 * @see StateObservableOperationMode
	 *
	 * @param mode Instructs the returned observable to complete after emitting the first value
	 *             or continue emitting changes until the subscription has been canceled. Defaults to
	 *             StateObservableOperationMode.Watch.
	 */
	public asStateObservable(mode: StateObservableOperationMode = StateObservableOperationMode.Watch): Observable<T> {
		const source = iif(
			() => this.initialized,
			VOID,
			this.fetchAndUpdateStateValue()
		) as unknown as Observable<void>;
		return source.pipe(
			mergeMap(() => {
				if (mode === StateObservableOperationMode.EmitOnce) {
					return of(this.stateValue);
				}
				return (this as any)
					.asObservable()
					.pipe(filter((value: T | undefined): value is T => value !== undefined));
			})
		) as Observable<T>;
	}

	/**
	 * Clears the current stateValue, marking the state subject as not initialized.
	 *
	 * NOTE: The `asStateObservable()` method will only emit defined values, use `asObservable()`
	 *       if undefined values are needed.
	 */
	public clearStateValue(): void {
		(this as any).next(undefined);
	}

	/**
	 * Returns the current stateValue, throws if the state has not been initialized.
	 */
	public getStateValue(): T {
		return this.stateValue;
	}

	/**
	 * Refreshes the value, subsequent calls resolve when the first still active call is resolved,
	 * thus avoiding unnecessary API calls.
	 *
	 * It's not necessary to initialize the state before using this method.
	 */
	public refresh(clearBeforeRefresh?: boolean): Observable<void> {
		return this.fetchAndUpdateStateValue(clearBeforeRefresh);
	}

	/**
	 * Refreshes the stateValue and de-duplicates concomitant requests.
	 */
	private fetchAndUpdateStateValue(clearBeforeRefresh?: boolean): Observable<void> {
		if (!this.sharedRefresher$) {
			// NOTES:
			// * The observables are built asynchronously to avoid issues with subsequent requests in the JS event loop.
			//   @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/EventLoop
			// * Using `of(undefined)` or `VOID` without scheduled(), will cause multiple tests to fail.
			const scheduled$: Observable<void> = scheduled(VOID, asyncScheduler);
			this.sharedRefresher$ = scheduled$.pipe(
				mergeMap((): Observable<T> => {
					if (clearBeforeRefresh) {
						this.clearStateValue();
					}
					return this.refresher();
				}),
				// NOTE: Only one value is taken from the refresher, because requiring a refresher that
				//       completes after one emitted value is unnecessarily complex.
				take(1),
				map((value: T): void => {
					(this as any).next(value);
				}),
				finalize((): void => {
					delete this.sharedRefresher$;
				}),
				share()
			);
		}
		return this.sharedRefresher$;
	}
}
